import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { useEffect, useMemo, useState } from 'react'
import type { CatalogDraft, CatalogRow } from './CatalogList.types'
import './CatalogList.css'

const STATUS_OPTIONS = ['Active', 'Inactive', 'Awarded']
const CONTRACT_TYPE_OPTIONS = ['GWAC', 'BPA', 'IDIQ']
const PRIME_SUB_JV_OPTIONS = ['Prime', 'Sub', 'JV']
const BUSINESS_SIZE_OPTIONS = ['Small', 'Large']
const SET_ASIDE_OPTIONS = ['HUBZone', 'SDVOSB', 'WOSB', 'EDWOSB', 'None']

export interface EditCatalogDrawerProps {
  open: boolean
  onClose: () => void
  row: CatalogRow | null
  onSave: (patch: Partial<CatalogRow>) => void
  onDelete: () => void
}

export function EditCatalogDrawer({ open, onClose, row, onSave, onDelete }: EditCatalogDrawerProps) {
  const [activeTab, setActiveTab] = useState(0)
  const [ctaOems, setCtaOems] = useState<string[]>([])
  const [ctaLoading, setCtaLoading] = useState(false)
  const [ctaSelected, setCtaSelected] = useState<Set<string>>(new Set())
  const [addOemOpen, setAddOemOpen] = useState(false)
  const [addOemName, setAddOemName] = useState('')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const [draft, setDraft] = useState<CatalogDraft>({
    contractVehicle: '',
    contractNumber: '',
    contractName: '',
    status: '',
    contractType: '',
    primeSubJV: '',
    businessSize: '',
    setAside: '',
    scope: '',
    popStart: '',
    popEnd: '',
    contractFee: '',
    contractFeeComments: '',
    partnerFee: '',
    partnerFeeComments: '',
    generalComments: '',
  })

  useEffect(() => {
    if (!open || !row) return
    setActiveTab(0)
    setDraft({
      contractVehicle: row.contractVehicle,
      contractNumber: row.contractNumber,
      contractName: row.contractName,
      status: row.status,
      contractType: row.contractType,
      primeSubJV: row.primeSubJV,
      businessSize: row.businessSize,
      setAside: row.setAside,
      scope: row.scope,
      popStart: row.popStart,
      popEnd: row.popEnd,
      contractFee: row.contractFee,
      contractFeeComments: row.contractFeeComments,
      partnerFee: row.partnerFee,
      partnerFeeComments: row.partnerFeeComments,
      generalComments: row.generalComments,
    })
  }, [open, row])

  useEffect(() => {
    if (!open || !row || activeTab !== 1) return
    const load = async () => {
      setCtaOems([])
      setCtaSelected(new Set())
      setCtaLoading(true)
      try {
        const { data } = await axios.get<string[]>(`/catalog/${row.id}/cta`)
        setCtaOems(data)
      } catch (err) {
        console.error('Failed to load CTA OEMs', err)
        setCtaOems([])
      } finally {
        setCtaLoading(false)
      }
    }
    load()
  }, [open, row, activeTab])

  async function handleAddCtaOem() {
    if (!row || !addOemName.trim()) return
    try {
      await axios.post(`/catalog/${row.id}/cta`, { oemName: addOemName.trim() })
      setCtaOems(prev => [...prev, addOemName.trim()].sort())
      setAddOemName('')
      setAddOemOpen(false)
    } catch (err) {
      console.error('Failed to add CTA OEM', err)
    }
  }

  async function handleDeleteCtaOems() {
    if (!row || ctaSelected.size === 0) return
    try {
      await axios.delete(`/catalog/${row.id}/cta`, { data: { oemNames: Array.from(ctaSelected) } })
      setCtaOems(prev => prev.filter(o => !ctaSelected.has(o)))
      setCtaSelected(new Set())
      setDeleteConfirmOpen(false)
    } catch (err) {
      console.error('Failed to delete CTA OEMs', err)
    }
  }

  const canSave = useMemo(() => {
    if (!row) return false
    if (draft.contractVehicle.trim().length === 0 || draft.contractNumber.trim().length === 0) return false
    return (Object.keys(draft) as (keyof CatalogDraft)[]).some(k => draft[k] !== row[k])
  }, [draft, row])

  const set = (key: keyof CatalogDraft) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setDraft(d => ({ ...d, [key]: e.target.value }))

  return (
    <Drawer anchor="right" open={open} onClose={onClose} className="catalog-drawer">
      {/* Header */}
      <div className="catalog-drawer-header">
        <Typography variant="h6" className="catalog-drawer-header-title">
          {row?.contractName ?? ''}
        </Typography>

        <div className="catalog-drawer-header-actions">
          <IconButton size="small" aria-label="Expand to full page">
            <ArrowForwardIcon />
          </IconButton>
          <IconButton size="small" onClick={onClose} aria-label="Close drawer">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <Divider />

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v as number)} className="catalog-drawer-tabs">
        <Tab label="EDIT FIELDS" />
        <Tab label="CTA OEM" />
      </Tabs>

      <Divider />

      {/* Tab content */}
      <div className="catalog-drawer-content">
        {activeTab === 0 && (
          <div className="catalog-drawer-form">
            <div className="catalog-drawer-fields">
              <TextField
                label="Contract Vehicle"
                size="small"
                value={draft.contractVehicle}
                onChange={set('contractVehicle')}
                fullWidth
              />

              <TextField
                label="Contract #"
                size="small"
                value={draft.contractNumber}
                onChange={set('contractNumber')}
                fullWidth
              />

              <TextField
                label="Contract Name"
                size="small"
                value={draft.contractName}
                onChange={set('contractName')}
                fullWidth
              />

              <TextField label="Status" size="small" select value={draft.status} onChange={set('status')} fullWidth>
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {STATUS_OPTIONS.map(o => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Contract Type"
                size="small"
                select
                value={draft.contractType}
                onChange={set('contractType')}
                fullWidth
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {CONTRACT_TYPE_OPTIONS.map(o => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Prime/Sub/JV"
                size="small"
                select
                value={draft.primeSubJV}
                onChange={set('primeSubJV')}
                fullWidth
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {PRIME_SUB_JV_OPTIONS.map(o => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Business Size"
                size="small"
                select
                value={draft.businessSize}
                onChange={set('businessSize')}
                fullWidth
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {BUSINESS_SIZE_OPTIONS.map(o => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Set Aside"
                size="small"
                select
                value={draft.setAside}
                onChange={set('setAside')}
                fullWidth
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {SET_ASIDE_OPTIONS.map(o => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </TextField>

              <TextField label="Scope" size="small" value={draft.scope} onChange={set('scope')} fullWidth />

              <TextField label="PoP Start" size="small" value={draft.popStart} onChange={set('popStart')} fullWidth />

              <TextField label="PoP End" size="small" value={draft.popEnd} onChange={set('popEnd')} fullWidth />

              <TextField
                label="Contract Fee"
                size="small"
                value={draft.contractFee}
                onChange={set('contractFee')}
                fullWidth
              />

              <TextField
                label="Contract Fee Comments"
                size="small"
                value={draft.contractFeeComments}
                onChange={set('contractFeeComments')}
                fullWidth
              />

              <TextField
                label="Partner Fee"
                size="small"
                value={draft.partnerFee}
                onChange={set('partnerFee')}
                fullWidth
              />

              <TextField
                label="Partner Fee Comments"
                size="small"
                value={draft.partnerFeeComments}
                onChange={set('partnerFeeComments')}
                fullWidth
              />

              <TextField
                label="General Comments"
                size="small"
                multiline
                minRows={3}
                value={draft.generalComments}
                onChange={set('generalComments')}
                fullWidth
              />
            </div>

            <div className="catalog-drawer-form-actions">
              <Button
                variant="contained"
                disabled={!canSave}
                onClick={() =>
                  onSave({
                    contractVehicle: draft.contractVehicle.trim(),
                    contractNumber: draft.contractNumber.trim(),
                    contractName: draft.contractName.trim(),
                    status: draft.status,
                    contractType: draft.contractType,
                    primeSubJV: draft.primeSubJV,
                    businessSize: draft.businessSize,
                    setAside: draft.setAside,
                    scope: draft.scope.trim(),
                    popStart: draft.popStart.trim(),
                    popEnd: draft.popEnd.trim(),
                    contractFee: draft.contractFee.trim(),
                    contractFeeComments: draft.contractFeeComments.trim(),
                    partnerFee: draft.partnerFee.trim(),
                    partnerFeeComments: draft.partnerFeeComments.trim(),
                    generalComments: draft.generalComments.trim(),
                  })
                }
              >
                SAVE
              </Button>
              <Button variant="outlined" color="error" onClick={onDelete}>
                DELETE
              </Button>
            </div>
          </div>
        )}

        {activeTab === 1 && (
          <div className="catalog-drawer-form">
            {ctaLoading ? (
              <div className="catalog-drawer-loading">
                <CircularProgress size={24} />
              </div>
            ) : (
              <>
                <div className="catalog-drawer-oem-actions">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setAddOemName('')
                      setAddOemOpen(true)
                    }}
                  >
                    ADD NEW
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={ctaSelected.size === 0}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    DELETE
                  </Button>
                </div>
                {ctaOems.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No OEMs found.
                  </Typography>
                ) : (
                  <div className="catalog-drawer-oem-grid">
                    {ctaOems.map(oem => (
                      <FormControlLabel
                        key={oem}
                        label={oem}
                        control={
                          <Checkbox
                            size="small"
                            checked={ctaSelected.has(oem)}
                            onChange={e => {
                              setCtaSelected(prev => {
                                const next = new Set(prev)
                                e.target.checked ? next.add(oem) : next.delete(oem)
                                return next
                              })
                            }}
                          />
                        }
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {/* Add CTA OEM Dialog */}
      <Dialog open={addOemOpen} onClose={() => setAddOemOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add CTA OEM</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Enter a name to add a new CTA OEM.</DialogContentText>
          <TextField
            autoFocus
            label="CTA OEM"
            size="small"
            fullWidth
            value={addOemName}
            onChange={e => setAddOemName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleAddCtaOem()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOemOpen(false)}>CANCEL</Button>
          <Button variant="contained" disabled={!addOemName.trim()} onClick={handleAddCtaOem}>
            ADD
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete CTA OEM Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Delete CTA OEM?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {ctaSelected.size === 1
              ? 'Are you sure you want to delete this CTA OEM? The action cannot be undone.'
              : 'Are you sure you want to delete these CTA OEMs? The action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>CANCEL</Button>
          <Button variant="contained" onClick={handleDeleteCtaOems}>
            DELETE
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  )
}
